import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { toListTop, useViewer } from '@/design';
import { readViewerChoice, writeViewerChoice } from '@/design/viewer-store';
import type { IPage } from '../../../api/oversight-contracts';
import { pageOf } from '../../../components/admin-page';
import { LIST_MAX_PAGE_SIZE } from '../../../lists';

/**
 * A work page's footer (SR-485) — Reinstatement, DD calls, Metrics updates, the compliance queues
 * and My tasks, as Salesforce's list views and Conga Grid draw theirs: always there, under the
 * table and inside the window's fit (SR-439), whatever the list holds. "1–25 of 34 · page 1 of 2",
 * *Per page*, *Load more* and *Previous* / *Next*.
 *
 * **Per page is the viewer's, one choice for every work page**, kept on this browser
 * (`veritant.perPage.<viewer>`), 25 until changed. Every API these pages read takes 100 a page at
 * least — the work items' and My tasks' ceiling; Lists' and the metrics' is 1,000 — so each choice
 * is one read.
 *
 * **Load more grows the rows shown by a page**: the next page's rows are read and drawn under the
 * ones shown, and the line says "1–50 of 340". From the list's first page and within the API's
 * ceiling that is one read of the longer page; further on, or past it, a read a page — at most
 * `MAX_SHOWN` rows at once. *Next* goes on from the last page shown; *Previous*, a page change, a
 * new Per page or a filter drop what was added.
 *
 * **The window is in the URL** (`useWorkPaging`): `page`, in the viewer's Per page, and `size`, the
 * rows shown where *Load more* added to the page — so Back from a record, and the list beside it
 * (`windowOf`), find the same rows. A filter change drops both (`dropPaging`).
 *
 * A press while a read is out is ignored, the button kept focusable (`aria-disabled`).
 *
 * **A new page starts at the top of the list** (SR-503): *Previous*, *Next* and a new Per page,
 * pressed with the table's top scrolled away, bring it back under the sticky bars (`toListTop`) —
 * the list's head, not the document's, so the controls above stay where the compact bar stands for
 * them. *Load more* keeps the place: its rows land under the ones read.
 *
 * Where the page scrolls rather than the table (`sticky`), it sticks to the window's bottom while
 * the table is in view.
 */

/** What Per page offers. */
export const PER_PAGE: readonly number[] = [25, 50, 100];

const DEFAULT_PER_PAGE = 25;

/** The viewer's Per page, and its setter — a stored value that is not one of `offered` is the
 *  largest offered below it (a page offering more than `PER_PAGE` chose it), else the default. */
export function usePerPage(offered: readonly number[] = PER_PAGE): [number, (size: number) => void] {
  const key = `veritant.perPage.${useViewer()}`;
  const [chosen, setChosen] = React.useState<Nullable<{ key: string; size: number }>>(null);
  const stored = Number(readViewerChoice(key).size);
  const below = offered.filter((option) => option <= stored);
  const size =
    chosen?.key === key && offered.includes(chosen.size)
      ? chosen.size
      : below.length > 0
        ? Math.max(...below)
        : DEFAULT_PER_PAGE;
  const choose = React.useCallback(
    (next: number) => {
      setChosen({ key, size: next });
      writeViewerChoice(key, { size: next });
    },
    [key]
  );
  return [size, choose];
}

/** The most rows a work page shows at once — survey-api's Lists ceiling, the bound on `size`. */
export const MAX_SHOWN = LIST_MAX_PAGE_SIZE;

/** The URL's keys of a work page's window. */
export const PAGING = ['page', 'size'] as const;

/** `params` without the window — a new filter starts at the top. Changes `params`, and answers it. */
export function dropPaging(params: URLSearchParams): URLSearchParams {
  for (const key of PAGING) params.delete(key);
  return params;
}

/** The window the URL names in `perPage` rows a page: its page, and the pages shown from it on. */
export function windowOf(params: URLSearchParams, perPage: number): { page: number; pages: number } {
  const shown = Math.min(MAX_SHOWN, Number(params.get('size')) || perPage);
  return { page: pageOf(params.get('page')), pages: Math.max(1, Math.round(shown / perPage)) };
}

export interface IWorkPaging {
  page: number;
  perPage: number;
  /** The pages shown from `page` on. */
  pages: number;
  setPage: (page: number) => void;
  setPerPage: (size: number) => void;
  more: () => void;
  /** `WorkPager`'s own props, to spread. */
  pager: Pick<
    React.ComponentProps<typeof WorkPager>,
    'page' | 'perPage' | 'perPages' | 'pages' | 'maxShown' | 'onPage' | 'onPerPage' | 'onMore'
  >;
}

/**
 * A work page's window, in the URL. `tab` scopes it to one section of a page that pages each
 * (Reinstatement): the window is `tab`'s where the URL names it or names no tab, and each write
 * names it. `replace` replaces the history entry rather than pushing one — the page's own way.
 * `perPages` is what the page's Per page offers, `PER_PAGE` unless it reads more at once.
 */
export function useWorkPaging({
  tab,
  replace = false,
  perPages = PER_PAGE
}: { tab?: string; replace?: boolean; perPages?: readonly number[] } = {}): IWorkPaging {
  const [params, setParams] = useSearchParams();
  const [perPage, choosePerPage] = usePerPage(perPages);
  const own = tab === undefined || !params.has('tab') || params.get('tab') === tab;
  const { page, pages } = own ? windowOf(params, perPage) : { page: 1, pages: 1 };
  const write = (nextPage: number, nextPages: number, size = perPage) =>
    setParams(
      (now) => {
        const next = dropPaging(new URLSearchParams(now));
        if (tab !== undefined) next.set('tab', tab);
        if (nextPage > 1) next.set('page', String(nextPage));
        if (nextPages > 1) next.set('size', String(nextPages * size));
        return next;
      },
      { replace }
    );
  const setPage = (next: number) => write(next, 1);
  const setPerPage = (size: number) => {
    choosePerPage(size);
    write(1, 1, size);
  };
  const more = () => write(page, Math.min(pages + 1, Math.floor(MAX_SHOWN / perPage)));
  return {
    page,
    perPage,
    pages,
    setPage,
    setPerPage,
    more,
    pager: { page, perPage, perPages, pages, maxShown: MAX_SHOWN, onPage: setPage, onPerPage: setPerPage, onMore: more }
  };
}

/** Pages `page` to `page + pages - 1` of `perPage` rows, in order: one read where they start the
 *  list and fit the API's `max`, else a read a page, side by side. */
export function readPages<T>(
  read: (page: number, pageSize: number) => Promise<T>,
  page: number,
  perPage: number,
  pages: number,
  max: number
): Promise<T[]> {
  if (pages === 1 || (page === 1 && pages * perPage <= max)) {
    return read(page, pages * perPage).then((answer) => [answer]);
  }
  return Promise.all(Array.from({ length: pages }, (_, at) => read(page + at, perPage)));
}

/** `readPages` over an API that answers `IPage`s: their items as one page of `perPage`. */
export async function readItemPages<P extends IPage<unknown>>(
  read: (page: number, pageSize: number) => Promise<P>,
  page: number,
  perPage: number,
  pages: number,
  max: number
): Promise<P> {
  const parts = await readPages(read, page, perPage, pages, max);
  return { ...parts[0], items: parts.flatMap((part) => part.items), page, pageSize: perPage };
}

/** The most a page of work items or My tasks holds — survey-api's `PageRequest` ceiling. */
export const WORK_ITEMS_MAX_PAGE = 100;

export function WorkPager({
  page,
  perPage,
  perPages = PER_PAGE,
  pages = 1,
  total,
  maxShown = Infinity,
  busy,
  onPage,
  onPerPage,
  onMore,
  sticky = false
}: {
  page: number;
  perPage: number;
  /** What Per page offers. */
  perPages?: readonly number[];
  /** The pages shown from `page` on — more than one after *Load more*. */
  pages?: number;
  /** The most rows shown at once: *Load more* is off where one more page would pass it. */
  maxShown?: number;
  total: number;
  /** A read is out: the dimmed buttons keep their focus, and a press does nothing. */
  busy?: boolean;
  onPage: (page: number) => void;
  onPerPage: (size: number) => void;
  onMore: () => void;
  sticky?: boolean;
}) {
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const end = Math.min(page + pages - 1, lastPage);
  const first = total === 0 ? 0 : (page - 1) * perPage + 1;
  const last = Math.min(end * perPage, total);
  // A page past the last — a pasted or stale address — holds nothing of a list that is not empty.
  const pastTheEnd = total > 0 && first > total;
  const self = React.useRef<HTMLDivElement>(null);
  const toTop = (act: () => void) => {
    toListTop(self.current);
    act();
  };
  const turn = (act: () => void) => () => toTop(act);
  const press = (off: boolean, act: () => void) => ({
    type: 'button' as const,
    variant: 'outline' as const,
    size: 'sm' as const,
    disabled: off,
    'aria-disabled': busy || undefined,
    className: 'aria-disabled:cursor-default aria-disabled:opacity-50',
    onClick: () => {
      if (!busy) act();
    }
  });
  return (
    <div
      ref={self}
      data-work-pager=""
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-2 text-[0.8125rem] text-muted-foreground',
        sticky && 'sticky bottom-0 z-10 bg-background py-2'
      )}
    >
      <span data-page-count="" className="tabular-nums">
        {total === 0
          ? 'Nothing to show'
          : pastTheEnd
            ? `Nothing on page ${page} · ${total.toLocaleString('en-US')} in all`
            : `${first.toLocaleString('en-US')}–${last.toLocaleString('en-US')} of ${total.toLocaleString('en-US')}`}
        {!pastTheEnd &&
          lastPage > 1 &&
          (end > page ? ` · pages ${page}–${end} of ${lastPage}` : ` · page ${page} of ${lastPage}`)}
      </span>
      <span className="flex-1" />
      <label className="flex items-center gap-1.5">
        Per page
        <select
          value={perPage}
          onChange={(event) => toTop(() => onPerPage(Number(event.target.value)))}
          className="h-7 rounded-md border border-border bg-white px-1.5 text-[0.8125rem] text-foreground"
        >
          {perPages.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
      <span className="flex gap-2">
        <Button {...press(pastTheEnd || last >= total || (pages + 1) * perPage > maxShown, onMore)}>Load more</Button>
        <Button {...press(page <= 1, turn(() => onPage(Math.min(page - 1, lastPage))))}>Previous</Button>
        <Button {...press(end >= lastPage, turn(() => onPage(end + 1)))}>Next</Button>
      </span>
    </div>
  );
}
